import {
  Megaphone, Palette, Code2, Wrench, ShoppingCart, LayoutDashboard, Sparkles, PenLine,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "digital-marketing",
    title: "Digital Marketing",
    tagline: "Full-funnel growth that compounds.",
    icon: Megaphone,
    summary: "Data-driven SEO, paid media, and content strategy that turns visitors into revenue.",
    features: ["SEO & Content Strategy", "Paid Ads (Google, Meta, LinkedIn)", "Email & Marketing Automation", "Analytics & Reporting", "Conversion Rate Optimization", "Social Media Management"],
    process: ["Audit & Discovery", "Strategy & Roadmap", "Launch Campaigns", "Optimize & Scale"],
    tiers: [
      { name: "Starter", price: "$399", per: "/month", desc: "For early-stage brands.", features: ["1 channel (SEO or Ads)", "Monthly report", "Up to 4 content pieces", "Email support"] },
      { name: "Growth", price: "$899", per: "/month", desc: "Multi-channel growth.", features: ["3 channels", "Bi-weekly reports", "8 content pieces", "CRO experiments", "Priority support"], featured: true },
      { name: "Scale", price: "$1,899", per: "/month", desc: "Aggressive scaling.", features: ["All channels", "Weekly reports", "Unlimited content", "Dedicated strategist", "Slack channel"] },
    ],
  },
  {
    slug: "website-design",
    title: "Website Design",
    tagline: "Interfaces that convert and delight.",
    icon: Palette,
    summary: "Distinctive, conversion-focused design systems that feel like your brand — not a template.",
    features: ["Brand-aligned UI System", "High-fidelity Prototypes", "Interaction & Motion Design", "Accessibility (WCAG AA)", "Design Handoff", "Design Reviews"],
    process: ["Discovery Workshop", "Wireframe & IA", "Visual Design", "Prototype & Handoff"],
    tiers: [
      { name: "Landing", price: "$699", per: "one-time", desc: "Single high-impact page.", features: ["Up to 6 sections", "2 revisions", "Mobile-first design", "Figma files"] },
      { name: "Marketing Site", price: "$1,899", per: "one-time", desc: "Up to 8 pages.", features: ["Custom design system", "Motion prototypes", "3 revisions", "Component library"], featured: true },
      { name: "Product Suite", price: "Custom", per: "quote", desc: "App + marketing.", features: ["Design system at scale", "Dashboard flows", "Design ops", "Ongoing partnership"] },
    ],
  },
  {
    slug: "website-development",
    title: "Website Development",
    tagline: "Fast, resilient, and future-proof.",
    icon: Code2,
    summary: "Modern stacks (React, Next.js, FastAPI) built for performance, SEO, and scale.",
    features: ["Custom Frontend (React/Next.js)", "API & Backend (FastAPI/Node)", "Headless CMS", "Performance Budgeting", "SEO Structure", "CI/CD Deployment"],
    process: ["Architecture Review", "Sprint Development", "QA & Performance", "Launch & Monitor"],
    tiers: [
      { name: "Static", price: "$999", per: "one-time", desc: "Marketing site build.", features: ["Up to 6 pages", "CMS optional", "Basic SEO", "1 month support"] },
      { name: "Dynamic", price: "$2,799", per: "one-time", desc: "CMS + custom features.", features: ["Headless CMS", "Custom modules", "3 months support", "Analytics setup"], featured: true },
      { name: "Application", price: "Custom", per: "quote", desc: "Full-stack product.", features: ["Custom architecture", "Auth & payments", "Ongoing engineering", "SLA & monitoring"] },
    ],
  },
  {
    slug: "website-maintenance",
    title: "Website Maintenance",
    tagline: "Peace of mind, on retainer.",
    icon: Wrench,
    summary: "Security patches, uptime monitoring, backups, and updates — so your site never breaks.",
    features: ["24/7 Uptime Monitoring", "Weekly Backups", "Security Patching", "Content Updates", "Performance Tuning", "Monthly Health Report"],
    process: ["Onboarding Audit", "Baseline Hardening", "Monthly Cycles", "Emergency Response"],
    tiers: [
      { name: "Basic", price: "$79", per: "/month", desc: "Essentials only.", features: ["Weekly backups", "Uptime monitor", "Security updates", "Email support"] },
      { name: "Standard", price: "$179", per: "/month", desc: "For growing sites.", features: ["Everything in Basic", "2 hrs content updates", "Speed audits", "Priority email"], featured: true },
      { name: "Premium", price: "$359", per: "/month", desc: "Mission critical.", features: ["24/7 response", "8 hrs updates", "Quarterly UX review", "Dedicated engineer"] },
    ],
  },
  {
    slug: "ecommerce-websites",
    title: "E-Commerce Websites",
    tagline: "Stores that sell — not just showcase.",
    icon: ShoppingCart,
    summary: "Shopify, WooCommerce, and custom storefronts built to maximize AOV and repeat purchases.",
    features: ["Storefront Design", "Payment & Shipping", "Product Catalog", "Cart & Checkout UX", "Analytics & Pixel Setup", "Subscription/Memberships"],
    process: ["Store Strategy", "Design & Build", "Migrate & Launch", "Optimize"],
    tiers: [
      { name: "Launch", price: "$1,299", per: "one-time", desc: "Up to 50 SKUs.", features: ["Theme customization", "Payment gateway", "Basic SEO", "1 month support"] },
      { name: "Scale", price: "$3,299", per: "one-time", desc: "Custom store.", features: ["Custom storefront", "500+ SKUs", "Subscriptions", "3 months support"], featured: true },
      { name: "Enterprise", price: "Custom", per: "quote", desc: "Headless & B2B.", features: ["Headless commerce", "ERP integrations", "Multi-currency", "Dedicated team"] },
    ],
  },
  {
    slug: "web-portals",
    title: "Web Portals",
    tagline: "Secure portals for teams, clients, and partners.",
    icon: LayoutDashboard,
    summary: "Custom dashboards, client portals, and internal tools with authentication and role-based access.",
    features: ["Role-Based Access", "Custom Dashboards", "Reporting & Exports", "Notifications", "Third-Party Integrations", "Audit Logs"],
    process: ["Requirements", "Prototype", "Build Sprints", "Launch"],
    tiers: [
      { name: "Team", price: "$2,299", per: "one-time", desc: "Small team portal.", features: ["Up to 3 roles", "Basic dashboards", "5 integrations", "2 months support"] },
      { name: "Business", price: "$4,799", per: "one-time", desc: "Full featured.", features: ["Unlimited roles", "Custom dashboards", "SSO", "6 months support"], featured: true },
      { name: "Enterprise", price: "Custom", per: "quote", desc: "Compliance ready.", features: ["SOC2/HIPAA prep", "Custom SLAs", "24/7 support", "Dedicated PM"] },
    ],
  },
  {
    slug: "logo-design",
    title: "Logo Design",
    tagline: "Marks with meaning and staying power.",
    icon: Sparkles,
    summary: "Distinctive logo systems and brand identities — from strategy to guidelines.",
    features: ["Brand Strategy", "Logo & Marks", "Type & Color System", "Guidelines Doc", "Social/Print Assets", "Trademark-Ready Files"],
    process: ["Brand Discovery", "Concepting", "Refinement", "Guidelines"],
    tiers: [
      { name: "Solo", price: "$249", per: "one-time", desc: "Wordmark + color.", features: ["3 concepts", "2 revisions", "Vector files", "Color palette"] },
      { name: "Brand", price: "$699", per: "one-time", desc: "Full identity.", features: ["5 concepts", "Full guidelines", "Type system", "Business card & letterhead"], featured: true },
      { name: "System", price: "$1,899", per: "one-time", desc: "Brand at scale.", features: ["Sub-brands", "Illustration system", "Motion mark", "Onboarding kit"] },
    ],
  },
  {
    slug: "content-writing",
    title: "Content Writing",
    tagline: "Words that rank, resonate, and convert.",
    icon: PenLine,
    summary: "SEO articles, product copy, and long-form thought leadership from experienced writers.",
    features: ["SEO Content", "Blog Articles & Pillar Pages", "Product & Landing Copy", "Whitepapers & Ebooks", "Editorial Calendars", "Editing & Fact-Checking"],
    process: ["Content Strategy", "Research & Outlines", "Writing & Editing", "Publish & Iterate"],
    tiers: [
      { name: "Essentials", price: "$279", per: "/month", desc: "4 posts / month.", features: ["4 articles (800–1200w)", "Keyword research", "1 revision each", "Editorial calendar"] },
      { name: "Content Engine", price: "$699", per: "/month", desc: "10 posts / month.", features: ["10 articles", "Pillar + cluster", "2 revisions", "Performance report"], featured: true },
      { name: "Authority", price: "$1,599", per: "/month", desc: "20+ posts / month.", features: ["20+ pieces", "Thought leadership", "Whitepapers", "Dedicated editor"] },
    ],
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);
